import React, {useEffect} from 'react';
import Form from 'react-bootstrap/Form';
import {CSSObject} from '@emotion/react';
import Select from 'react-select';
import useData from '@/utils/useData';
import {ChartQuery, ChartOutput} from 'entities/chart';
import {FormikT, FormikTValues} from 'utils/types';
import {aggregationChoicesFilters, EventFilter, EventFilterGroup} from '@/views/storyboard/utils/chartConstants';
import {OptionProps} from 'react-select/dist/declarations/src/components/Option';
import {SingleValue} from 'react-select/dist/declarations/src/types';
import i18n from 'i18n';

interface Option {
    value: string;
    label: string;
    type?: string;
}

interface Props {
    values: FormikTValues<ChartQuery>;
    setFieldValue: FormikT<ChartQuery>['setFieldValue'];
    eventFilters?: EventFilter[];
    eventFiltersLoading?: boolean;
}

const ChartPivotColumnSelect = React.memo<Props>((
    {
        values,
        setFieldValue,
        eventFilters: eventFiltersProp,
        eventFiltersLoading,
    },
) => {
    const [eventFilterGroupList, loadingFromData] = useData<EventFilterGroup>(window.URLS.eventFiltersResource, {
        storeResolver: 'eventFilters',
        actionResolver: 'loadEventFilters',
        useCachedData: true,
    });

    const eventFilters: EventFilter[] = eventFiltersProp ?? [
        ...(eventFilterGroupList.core || []),
        ...(eventFilterGroupList.prioritised || []),
        ...(eventFilterGroupList.non_prioritised || []),
        ...(eventFilterGroupList.others || []),
    ];
    const loading = eventFiltersProp !== undefined ? (eventFiltersLoading ?? false) : loadingFromData;

    useEffect(() => {
        if (loading || values.output !== ChartOutput.PivotTerms || eventFilters.length === 0) {
            return;
        }

        const availableOptions = aggregationChoicesFilters[ChartOutput.PivotTerms](eventFilters)
            .filter((el) => el[4] === true)
            .filter((el) => el[0] !== values.aggs_item);

        if (!values.pivot_col_aggs_item || values.pivot_col_aggs_item === values.aggs_item) {
            const nextValue = availableOptions[0]?.[0] ?? '';
            if (nextValue && nextValue !== values.pivot_col_aggs_item) {
                setFieldValue('pivot_col_aggs_item', nextValue, false);
            }
        }
    }, [loading, eventFilters, values.output, values.pivot_col_aggs_item, values.aggs_item, setFieldValue]);

    return (
        <Form.Group className="cs-field" controlId="pivotColAggsItem">
            <label className="cs-field__label">{i18n.t('pivot_column_field.text')}</label>
            <Select<Option>
                id="chart-settings-form-pivot-column"
                value={{
                    value: values.pivot_col_aggs_item || '',
                    label: eventFilters.find((x) => x[0] === values.pivot_col_aggs_item)?.[1] ?? '',
                }}
                name="pivot_col_aggs_item"
                onChange={(value: SingleValue<Option>) => setFieldValue('pivot_col_aggs_item', value?.value ?? '')}
                isLoading={loading && !values.pivot_col_aggs_item}
                loadingMessage={() => i18n.t('jt_loading_opt.text')}
                maxMenuHeight={180}
                menuPlacement="auto"
                menuShouldScrollIntoView={true}
                options={aggregationChoicesFilters[ChartOutput.PivotTerms](eventFilters)
                    .filter((el) => el[4] === true)
                    .filter((el) => el[0] !== values.aggs_item)
                    .map((el) => ({
                        value: el[0],
                        label: el[1],
                        type: el[2],
                    }))}
                styles={{
                    control: (base: CSSObject): CSSObject => ({...base, minHeight: 34, height: 34}),
                    valueContainer: (base: CSSObject): CSSObject => ({...base, padding: '0 8px'}),
                    indicatorsContainer: (base: CSSObject): CSSObject => ({...base, height: 34}),
                    menu: (base: CSSObject): CSSObject => ({...base, marginBottom: 8}),
                    menuList: (base: CSSObject): CSSObject => ({...base, maxHeight: 180}),
                    option: (base: CSSObject, state: OptionProps<Option, false>): CSSObject => {
                        let additionalStyles: CSSObject;
                        if (!state.isFocused) {
                            additionalStyles = {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            };
                        } else {
                            additionalStyles = {
                                wordBreak: 'break-word',
                            };
                        }
                        return {
                            ...base,
                            background: state.isFocused ? 'rgba(0, 158, 251, 0.08)' : 'transparent',
                            backgroundColor: state.isFocused ? 'rgba(0, 158, 251, 0.08)' : 'transparent',
                            color: 'inherit',
                            cursor: 'pointer',
                            ':active': {
                                ...base[':active'],
                                backgroundColor: 'rgba(0, 158, 251, 0.08)',
                            },
                            ...additionalStyles,
                        };
                    },
                }}
            />
        </Form.Group>
    );
});

export default ChartPivotColumnSelect;
